import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import axios from 'axios';
import { isAuthenticated, logout as authLogout, getToken } from '../services/authService'; // Assuming authLogout handles token clearing
import { getMyProfile, getMyProfileV2 } from '../services/businessService';

import { useTranslation } from 'react-i18next';

const UserContext = createContext(undefined);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true); // Start with true if auto-fetching
  const [errorUser, setErrorUser] = useState(null);
  const { i18n } = useTranslation(); // For language switching

  const fetchUserData = useCallback(async (forceRefresh = false) => {
    console.log('[UserContext] fetchUserData called with forceRefresh =', forceRefresh);

    if (!forceRefresh) {
      try {
        const cachedDataString = localStorage.getItem('userProfile');
        if (cachedDataString) {
          const cachedData = JSON.parse(cachedDataString);
          console.log('[UserContext] Cached user profile found in localStorage and setUserData :', cachedData);
          setUserData(cachedData);
          if (i18n.language !== cachedData.language) {
            i18n.changeLanguage(cachedData.language);
          }
          setLoadingUser(false);
          setErrorUser(null); // Clear previous errors if loaded from cache
          console.log('User data loaded from localStorage');
        }

        // Same for userProfileV2
        const cachedProfileString = localStorage.getItem('userProfileV2');
        if (cachedProfileString) {
          const cachedProfile = JSON.parse(cachedProfileString);
          console.log('[UserContext] Cached user profilev2 found in localStorage and setUserProfile :', cachedProfile);
          setUserProfile(cachedProfile);
          if (i18n.language !== cachedProfile.language) {
            i18n.changeLanguage(cachedProfile.language);
          }
          setLoadingUser(false);
          setErrorUser(null); // Clear previous errors if loaded from cache
          console.log('User profile data loaded from localStorage');
          return; // Exit if loaded from cache and no forceRefresh
        }

         // Exit if loaded from cache and no forceRefresh
      } catch (error) {
        console.error("Error reading user profile from localStorage:", error);
        // Proceed to fetch from API if localStorage read fails
      }
    }

    // Execute this part if forceRefresh is true or if no cache was found and access token exists
    const authenticated = await isAuthenticated();
    if (!authenticated) {
      console.warn('UserContext: User is not authenticated. Skipping fetchUserData.');
      setUserData(null);
      setUserProfile(null);
      setLoadingUser(false);
      return;
    }
    
    const token = getToken();
    if (token) {
      console.log('[UserContext] User is authenticated, has token , fetching user data.');
      // If not in cache, or if forceRefresh is true, or if localStorage read failed:
      setLoadingUser(true);
      try {
        // const response = await axios.get(`${BACKEND_URL}/app/api/my-profile/`, {
        //   headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        // });
        // const profileData = response.data;
        const profileData = await getMyProfile();
        console.log('[UserContext] FetchAPI user profile found in localStorage and setUserData :', profileData);
        setUserData(profileData);
        try {
          localStorage.setItem('userProfile', JSON.stringify(profileData));
          console.log('User data fetched from API and saved to localStorage');
        } catch (error) {
          console.error("Error saving user profile to localStorage:", error);
        }
        if (i18n.language !== profileData.language) {
          i18n.changeLanguage(profileData.language);
        }
        setErrorUser(null);
      } catch (err) {
        console.error("[UserContext] Failed to fetch user data from API:", err);
        setErrorUser(err);
        // Optionally, clear stale cache if API fetch fails, or handle based on error type
        // For instance, if it's a 401, the user might be logged out elsewhere.
        // localStorage.removeItem('userProfile'); 
      } finally {
        setLoadingUser(false);
      }

      setLoadingUser(true);
      try {
        // const response = await axios.get(`${BACKEND_URL}/app/api/profile/`, {
        //   headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        // });
        // const userProfileV2Data = response.data;
        const userProfileV2Data = await getMyProfileV2();
        console.log('[UserContext] FetchAPI user profile found in localStorage and setUserProfile :', userProfileV2Data);
        setUserProfile(userProfileV2Data);

        try {
          localStorage.setItem('userProfileV2', JSON.stringify(userProfileV2Data));
          console.log('User data fetched from API and saved to localStorage');
        } catch (error) {
          console.error("Error saving user profile to localStorage:", error);
        }
        if (i18n.language !== userProfileV2Data.language) {
          i18n.changeLanguage(userProfileV2Data.language);
        }
        setErrorUser(null);
      } catch (err) {
        console.error("[UserContext] Failed to fetch userProfileV2Data from API:", err);
        setErrorUser(err);
        // Optionally, clear stale cache if API fetch fails, or handle based on error type
        // For instance, if it's a 401, the user might be logged out elsewhere.
        // localStorage.removeItem('userProfile'); 
      } finally {
        setLoadingUser(false);
      }
    } else {
      console.warn('UserContext: No access token found. Cannot fetch user data.');
      setUserData(null);
      setUserProfile(null);
      setLoadingUser(false);
    }

  }, [i18n]); // i18n is a dependency

  const clearUserData = useCallback(() => {
    setUserData(null);
    // Also clear from localStorage
    try {
      localStorage.removeItem('userProfile');
      localStorage.removeItem('selected_tenant_id');
      localStorage.removeItem('tenantData');
      localStorage.removeItem('tenants_allowed');
      localStorage.removeItem('userProfileV2');
      authLogout(); // Call the logout function to clear tokens
      console.log('User data cleared from localStorage');
    } catch (error) {
      console.error("Error removing user profile from localStorage:", error);
    }
    // setLoadingUser(false); // Reset loading state if needed
    // setErrorUser(null); // Reset error state
  }, []);

  // Effect to load user data on mount
  useEffect(() => {
    console.log('[UserContext] UserProvider mounted, checking authentication status.');
    const checkAuth = async () => {
      const authenticated = await isAuthenticated();
      if (authenticated) {
        console.log('[UserContext] User is authenticated on mount, fetching user data.');
        fetchUserData();
      } else {
        console.log('[UserContext] User is not authenticated on mount, clearing user data.');
        clearUserData();
      }
    };
    checkAuth();
  }, [fetchUserData, clearUserData]);

  // Ajout d'un listener sur l'event 'tokenChanged' pour relancer le fetch
  // useEffect(() => {
  //   const handler = () => {
  //     setLoadingUser(true); // Toujours repasser à true pour forcer le re-render
  //     fetchUserData(true); // Force le fetch depuis l'API
  //   };
  //   window.addEventListener('tokenChanged', handler);
  //   return () => {
  //     window.removeEventListener('tokenChanged', handler);
  //   };
  // }, [fetchUserData]);
  const contextValue = { userData, userProfile, loadingUser, errorUser, fetchUserData, clearUserData };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};
